import React, { createContext, useContext, type ReactNode } from 'react';
import { hasPermission, type Permissions, type UserRole } from '../utils/abac';

interface BusinessGroupPermissionContextValue {
  businessGroupId: string;
  userRoles: UserRole[];
}

const BusinessGroupPermissionContext = createContext<
  BusinessGroupPermissionContextValue | undefined
>(undefined);

export function useBusinessGroupPermission() {
  return useContext(BusinessGroupPermissionContext);
}

interface BusinessGroupPermissionProviderProps {
  businessGroupId: string;
  userRoles: UserRole[];
  children: ReactNode;
}

export function BusinessGroupPermissionProvider({
  businessGroupId,
  userRoles,
  children,
}: BusinessGroupPermissionProviderProps) {
  const value = React.useMemo(() => ({ businessGroupId, userRoles }), [businessGroupId, userRoles]);

  return (
    <BusinessGroupPermissionContext.Provider value={value}>
      {children}
    </BusinessGroupPermissionContext.Provider>
  );
}

interface WithBusinessGroupPermissionProps<Resource extends keyof Permissions> {
  resource: Resource;
  action: Permissions[Resource]['action'];
  data?: Permissions[Resource]['dataType'];
  children: ReactNode;
  fallback?: ReactNode;
}

export function WithBusinessGroupPermission<Resource extends keyof Permissions>({
  resource,
  action,
  data,
  children,
  fallback = null,
}: WithBusinessGroupPermissionProps<Resource>) {
  const context = useBusinessGroupPermission();

  if (!context) {
    // No context means we can't determine permissions - deny by default
    return fallback;
  }

  const { businessGroupId, userRoles } = context;

  // For resources that need business group context, inject it
  // The permission functions only check the businessGroup field, not full entity validity
  let permitted = false;

  if (
    resource === 'assignments' ||
    resource === 'products' ||
    resource === 'users' ||
    resource === 'configUsers'
  ) {
    const mockData = data || { businessGroup: businessGroupId };
    permitted = hasPermission(
      userRoles,
      resource,
      action,
      mockData as Permissions[Resource]['dataType'],
    );
  } else {
    permitted = hasPermission(userRoles, resource, action, data);
  }

  return permitted ? children : fallback;
}
